import { Page, scenarioI18n } from '@app-builder/components';
import { BreadCrumbLink, type BreadCrumbProps, BreadCrumbs } from '@app-builder/components/Breadcrumbs';
import { Callout } from '@app-builder/components/Callout';
import { FormErrorOrDescription } from '@app-builder/components/Form/Tanstack/FormErrorOrDescription';
import { DeleteRule } from '@app-builder/components/Scenario/Rules/Actions/DeleteRule';
import { DuplicateRule } from '@app-builder/components/Scenario/Rules/Actions/DuplicateRule';
import { AiDescription } from '@app-builder/components/Scenario/Rules/AiDescription';
import { AiGenerateRule } from '@app-builder/components/Scenario/Rules/AiGenerateRule';
import { FieldAstFormula } from '@app-builder/components/Scenario/Screening/FieldAstFormula';
import { FieldRuleGroup } from '@app-builder/components/Scenario/Screening/FieldRuleGroup';
import {
  useDerivedIterationRuleGroupsData,
  useDetectionScenarioData,
  useDetectionScenarioIterationData,
} from '@app-builder/hooks/routes-layout-data';
import useIntersection from '@app-builder/hooks/useIntersection';
import { authMiddleware } from '@app-builder/middlewares/auth-middleware';
import { AstNode, NewEmptyRuleAstNode } from '@app-builder/models';
import { useRuleDescriptionMutation } from '@app-builder/queries/scenarios/rule-description';
import { getBuilderOptionsFn } from '@app-builder/server-fns/scenarios';
import { useEditorMode } from '@app-builder/services/editor/editor-mode';
import { isAiRuleBuildingAvailable } from '@app-builder/services/feature-access';
import { useGetScenarioErrorMessage } from '@app-builder/services/validation';
import {
  collectRuleValidationMessages,
  findRuleValidation,
  hasRuleErrors,
} from '@app-builder/services/validation/scenario-validation';
import { getFieldErrors, handleSubmit } from '@app-builder/utils/form';
import { fromSUUIDtoUUID, fromUUIDtoSUUID, useParam } from '@app-builder/utils/short-uuid';
import { useDebouncedCallbackRef } from '@marble/shared';
import { useForm, useStore } from '@tanstack/react-form';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Button, cn, NumberInput, Tag, UnstyledInput } from 'ui-design-system';
import { Icon } from 'ui-icons';
import { z } from 'zod/v4';

const paramsSchema = z.object({
  scenarioId: z.string().transform((id) => fromSUUIDtoUUID(id)),
  ruleId: z.string().transform((id) => fromSUUIDtoUUID(id)),
});

const ruleLoader = createServerFn({ method: 'GET' })
  .middleware([authMiddleware])
  .validator(paramsSchema)
  .handler(async function ruleLoader({ data, context }) {
    const { scenarioIterationRuleRepository, entitlements } = context.authInfo;

    const [rule, builderOptions] = await Promise.all([
      scenarioIterationRuleRepository.getRule({ ruleId: data.ruleId }),
      getBuilderOptionsFn({ data: { scenarioId: data.scenarioId } }),
    ]);

    return {
      rule,
      isAiRuleDescriptionEnabled: isAiRuleBuildingAvailable(entitlements),
      builderOptions,
    };
  });

const editRuleFormSchema = z.object({
  name: z.string().nonempty(),
  description: z.string().optional(),
  ruleGroup: z.string().optional(),
  scoreModifier: z.number().int().min(-1000).max(1000),
  formula: z.any(),
});

type EditRuleForm = z.infer<typeof editRuleFormSchema>;

const editRuleConfigurationSchema = z.object({
  params: z.object({
    scenarioId: z.string(),
    iterationId: z.string(),
    ruleId: z.string(),
  }),
  payload: editRuleFormSchema,
});

const editRuleAction = createServerFn({ method: 'POST' })
  .middleware([authMiddleware])
  .validator(editRuleConfigurationSchema)
  .handler(async function editRuleAction({ context, data: { params, payload } }) {
    const { scenarioIterationRuleRepository } = context.authInfo;

    return await scenarioIterationRuleRepository.updateRule({
      ruleId: params.ruleId,
      ...payload,
    });
  });

export const Route = createFileRoute('/_app/_builder/detection/scenarios/$scenarioId/i/$iterationId/rules/$ruleId')({
  loader: ({ params }) => ruleLoader({ data: params }),
  staticData: {
    BreadCrumbs: [
      ({ isLast }: BreadCrumbProps) => {
        const { t } = useTranslation(['navigation']);
        const scenarioId = useParam('scenarioId');
        const iterationId = useParam('iterationId');

        return (
          <BreadCrumbLink
            isLast={isLast}
            to="/detection/scenarios/$scenarioId/i/$iterationId/rules"
            params={{ scenarioId: fromUUIDtoSUUID(scenarioId), iterationId: fromUUIDtoSUUID(iterationId) }}
          >
            {t('navigation:scenario.rules')}
          </BreadCrumbLink>
        );
      },
      ({ isLast }: BreadCrumbProps) => {
        const { t } = useTranslation(['common']);
        const { rule } = Route.useLoaderData();
        const scenarioId = useParam('scenarioId');
        const iterationId = useParam('iterationId');
        const editorMode = useEditorMode();

        return (
          <div className="flex items-center gap-sm">
            <BreadCrumbLink
              isLast={isLast}
              to="/detection/scenarios/$scenarioId/i/$iterationId/rules/$ruleId"
              params={{
                scenarioId: fromUUIDtoSUUID(scenarioId),
                iterationId: fromUUIDtoSUUID(iterationId),
                ruleId: fromUUIDtoSUUID(rule.id),
              }}
            >
              {rule.name ?? fromUUIDtoSUUID(rule.id)}
            </BreadCrumbLink>
            {editorMode === 'edit' ? <Tag size="big">{t('common:edit')}</Tag> : null}
          </div>
        );
      },
    ],
  },
  component: RuleDetail,
});

function RuleDetail() {
  const { isAiRuleDescriptionEnabled, rule, builderOptions } = Route.useLoaderData();

  const { t } = useTranslation([...scenarioI18n, 'common']);
  const iterationId = useParam('iterationId');
  const scenarioId = useParam('scenarioId');
  const router = useRouter();

  const mutation = useMutation({
    mutationFn: (value: EditRuleForm) =>
      editRuleAction({ data: { params: { scenarioId, iterationId, ruleId: rule.id }, payload: value } }),
    onSuccess: async () => {
      await router.invalidate();
      toast.success(t('common:success.save'));
      // TODO: wait for second thought, we might not need to navigate back to the rules list
      // router.navigate({
      //   to: '/detection/scenarios/$scenarioId/i/$iterationId/rules',
      //   params: { scenarioId: fromUUIDtoSUUID(scenarioId), iterationId: fromUUIDtoSUUID(iterationId) },
      // });
    },
    onError: () => {
      toast.error(t('common:errors.unknown'));
    },
  });

  const { currentScenario } = useDetectionScenarioData();
  const { scenarioValidation } = useDetectionScenarioIterationData();
  const getScenarioErrorMessage = useGetScenarioErrorMessage();
  const editor = useEditorMode();
  const ruleGroups = useDerivedIterationRuleGroupsData();

  useEffect(() => {
    if (scenarioValidation.rules.ruleItems[rule.id] === undefined) {
      void router.invalidate();
    }
  }, [rule.id, scenarioValidation, router]);

  const ruleValidation = useMemo(() => findRuleValidation(scenarioValidation, rule.id), [scenarioValidation, rule.id]);

  const nameInputRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const intersection = useIntersection(descriptionRef, {
    root: containerRef.current,
    rootMargin: '-30px',
    threshold: 1,
  });

  useEffect(() => {
    if (!rule.name && editor === 'edit') {
      nameInputRef.current?.focus();
    }
  }, []);

  const form = useForm({
    onSubmit: ({ value, formApi }) => {
      if (serverValidationMessages.length > 0 || !formApi.state.isValid) {
        return;
      }

      mutation.mutate(value);
    },
    validators: {
      onChange: editRuleFormSchema,
      onSubmit: editRuleFormSchema,
    },
    defaultValues: rule as EditRuleForm,
  });

  const formFormula = useStore(form.store, (state) => state.values.formula);

  const serverValidationMessages = useMemo(() => {
    if (!hasRuleErrors(ruleValidation, { formFormula })) {
      return [];
    }
    return collectRuleValidationMessages(ruleValidation, getScenarioErrorMessage, t('scenarios:edit_rule.formula'), {
      formFormula,
    });
  }, [ruleValidation, formFormula, getScenarioErrorMessage, t]);

  const hasFormulaValidationError = serverValidationMessages.length > 0;

  const [formulaKey, setFormulaKey] = useState(0);

  const ruleDescriptionMutation = useRuleDescriptionMutation(rule.id);
  const [ruleDescription, setRuleDescription] = useState<string | undefined>(undefined);
  const [isDebouncing, setIsDebouncing] = useState(false);

  useEffect(() => {
    if (!isAiRuleDescriptionEnabled) return;

    setRuleDescription(undefined);
    if (rule.formula) {
      ruleDescriptionMutation.mutateAsync({ scenarioId: currentScenario.id, astNode: rule.formula }).then((res) => {
        if (res.success && !ruleDescription && res.data.isRuleValid) {
          setRuleDescription(res.data.description);
        }
      });
    }
  }, [rule.id]);

  const innerHandleFormulaChange = useDebouncedCallbackRef((value: AstNode | undefined) => {
    setIsDebouncing(false);
    if (value) {
      ruleDescriptionMutation.mutateAsync({ scenarioId: currentScenario.id, astNode: value }).then((res) => {
        if (res.success && res.data.isRuleValid) {
          setRuleDescription(res.data.description);
        }
      });
    }
  }, 3000);
  const handleFormulaChange = (value: AstNode | undefined) => {
    if (!isAiRuleDescriptionEnabled) return;

    setIsDebouncing(true);
    innerHandleFormulaChange(value);
  };

  return (
    <Page.Main>
      <Page.Header>
        <BreadCrumbs
          back={`/detection/scenarios/${fromUUIDtoSUUID(currentScenario.id)}/i/${fromUUIDtoSUUID(iterationId)}/rules`}
        />
      </Page.Header>
      <Page.Container>
        <Page.Content width="table">
          <form className="relative flex flex-col" onSubmit={handleSubmit(form)}>
            <div
              className={cn(
                'bg-surface-page sticky top-0 flex h-[88px] items-center justify-between gap-md max-w-3xl',
                {
                  'border-b-grey-border border-b': !intersection?.isIntersecting,
                },
              )}
            >
              <form.Field
                name="name"
                validators={{
                  onChange: editRuleFormSchema.shape.name,
                  onBlur: editRuleFormSchema.shape.name,
                }}
              >
                {(field) => (
                  <div className="flex w-full flex-col gap-xs">
                    <UnstyledInput
                      ref={nameInputRef}
                      type="text"
                      name={field.name}
                      disabled={editor === 'view'}
                      defaultValue={field.state.value}
                      onChange={(e) => field.handleChange(e.currentTarget.value)}
                      onBlur={field.handleBlur}
                      className={cn(
                        'text-grey-primary text-l w-full border-none bg-transparent font-normal outline-hidden',
                        field.state.meta.errors.length > 0 && 'border-b border-red-primary',
                      )}
                      placeholder={t('scenarios:edit_rule.name_placeholder')}
                    />
                    {field.state.meta.errors.length > 0 ? (
                      <span className="text-xs text-red-primary">{t('scenarios:edit_rule.name_required')}</span>
                    ) : null}
                  </div>
                )}
              </form.Field>
              {editor === 'edit' ? (
                <div className="flex items-center gap-sm">
                  <DuplicateRule ruleId={rule.id} iterationId={rule.scenarioIterationId} scenarioId={scenarioId}>
                    <Button variant="secondary" size="small" type="button">
                      <Icon icon="copy" className="size-4" aria-hidden />
                      {t('scenarios:clone_rule.button')}
                    </Button>
                  </DuplicateRule>
                  <DeleteRule ruleId={rule.id} iterationId={rule.scenarioIterationId} scenarioId={scenarioId}>
                    <Button variant="destructive" size="small" type="button">
                      <Icon icon="delete" className="size-4" aria-hidden />
                      {t('common:delete')}
                    </Button>
                  </DeleteRule>
                  <Button variant="primary" size="small" type="submit">
                    <Icon icon="save" className="size-4" aria-hidden />
                    {t('common:save')}
                  </Button>
                </div>
              ) : null}
            </div>
            {serverValidationMessages.length > 0 ? (
              <Callout color="red" icon="lightbulb" iconColor="red" className="max-w-3xl">
                <ul className="flex flex-col gap-xs ps-md">
                  {serverValidationMessages.map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              </Callout>
            ) : null}
            <div className="flex flex-col gap-xl">
              <div className="border-grey-border flex flex-col items-start gap-lg border-b pb-lg max-w-3xl">
                <form.Field
                  name="description"
                  validators={{
                    onChange: editRuleFormSchema.shape.description,
                    onBlur: editRuleFormSchema.shape.description,
                  }}
                >
                  {(field) => (
                    <div ref={descriptionRef} className="flex w-full flex-col gap-xs">
                      <textarea
                        name={field.name}
                        disabled={editor === 'view'}
                        defaultValue={field.state.value}
                        onChange={(e) => field.handleChange(e.currentTarget.value)}
                        onBlur={field.handleBlur}
                        className="form-textarea text-grey-primary text-s w-full resize-none border-none bg-transparent font-medium outline-hidden"
                        placeholder={t('scenarios:edit_rule.description_placeholder')}
                      />
                      <FormErrorOrDescription errors={getFieldErrors(field.state.meta.errors)} />
                    </div>
                  )}
                </form.Field>
                <form.Field
                  name="ruleGroup"
                  validators={{
                    onChange: editRuleFormSchema.shape.ruleGroup,
                    onBlur: editRuleFormSchema.shape.ruleGroup,
                  }}
                >
                  {(field) => (
                    <div className="flex flex-col gap-sm">
                      <FieldRuleGroup
                        disabled={editor === 'view'}
                        onChange={field.handleChange}
                        onBlur={field.handleBlur}
                        selectedRuleGroup={field.state.value}
                        ruleGroups={ruleGroups}
                      />
                      <FormErrorOrDescription errors={getFieldErrors(field.state.meta.errors)} />
                    </div>
                  )}
                </form.Field>
              </div>
              <div className="flex flex-col">
                <span className="text-s font-medium">{t('scenarios:edit_rule.formula')}</span>
                <div className="grid grid-cols-[var(--container-3xl)_1fr] gap-sm">
                  <div
                    className={cn(
                      'bg-surface-card border-grey-border rounded-md border p-lg max-w-3xl',
                      hasFormulaValidationError && 'border-red-primary',
                    )}
                  >
                    <form.Field
                      name="formula"
                      validators={{
                        onChange: editRuleFormSchema.shape.formula,
                        onBlur: editRuleFormSchema.shape.formula,
                      }}
                    >
                      {(field) => (
                        <FieldAstFormula
                          key={formulaKey}
                          type="rule"
                          scenarioId={currentScenario.id}
                          options={builderOptions}
                          onBlur={field.handleBlur}
                          onChange={(node) => {
                            field.handleChange(node);
                            handleFormulaChange(node);
                          }}
                          astNode={field.state.value}
                          defaultValue={NewEmptyRuleAstNode()}
                        />
                      )}
                    </form.Field>
                  </div>

                  {isAiRuleDescriptionEnabled ? (
                    <AiDescription
                      isPending={isDebouncing || ruleDescriptionMutation.isPending}
                      description={ruleDescription}
                      className="self-start max-w-2xl"
                    />
                  ) : null}

                  {isAiRuleDescriptionEnabled && editor === 'edit' ? (
                    <AiGenerateRule
                      scenarioId={currentScenario.id}
                      ruleId={rule.id}
                      onFormulaGenerated={(ruleAst) => {
                        form.setFieldValue('formula', ruleAst);
                        handleFormulaChange(ruleAst);
                        setFormulaKey((k) => k + 1);
                      }}
                    />
                  ) : null}
                </div>
                <div className="bg-surface-card border-grey-border rounded-md border p-lg max-w-3xl">
                  <div className="flex items-center gap-sm">
                    <span className="bg-grey-background text-grey-secondary dark:text-grey-secondary text-s inline-flex rounded-sm p-sm font-medium">
                      {t('scenarios:edit_rule.score_heading')}
                    </span>
                    <form.Field
                      name="scoreModifier"
                      validators={{
                        onChange: editRuleFormSchema.shape.scoreModifier,
                        onBlur: editRuleFormSchema.shape.scoreModifier,
                      }}
                    >
                      {(field) => (
                        <div className="flex flex-col gap-xs">
                          <NumberInput
                            type="number"
                            name={field.name}
                            value={field.state.value}
                            onBlur={field.handleBlur}
                            disabled={editor === 'view'}
                            onChange={field.handleChange}
                            borderColor={field.state.meta.errors?.length === 0 ? 'greyfigma-90' : 'redfigma-47'}
                          />
                          <FormErrorOrDescription errors={getFieldErrors(field.state.meta.errors)} />
                        </div>
                      )}
                    </form.Field>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </Page.Content>
      </Page.Container>
    </Page.Main>
  );
}
